import { makeAutoObservable, reaction } from "mobx";
import { SurveyDto } from "../models/surveyDto";
import { QuestionDto } from "../models/questionDto";
import { QuestionChoiceDto } from "../models/questionChoiceDto";
import { SurveySectionDto } from "../models/surveySectionDto";
import { UserSurveyResultDto } from "../models/userSurveyResultDto";
import { UserSurveyQuestionResultDto } from "../models/userSurveyQuestionResultDto";
import SurveyContentTranslations from "../models/surveyContentTranslations";
import { UserSurveySectionResultDto } from "../models/userSurveySectionResultDto";

export default class CommonStore {

    lang: string | null = window.localStorage.getItem("lang");
    currentSurveyCode: string | null = null;
    currentSurveyId: number = 0;
    currentUserSurveyId: number = 0;
    surveyContentTranslations: SurveyContentTranslations | null = null;

    defaultLang = "en";
    appLoaded = false;

    constructor() {
        makeAutoObservable(this);
        reaction(
            () => this.lang,
            (lang) => {
                if (lang) {
                    window.localStorage.setItem("lang", lang);
                }
                else { window.localStorage.removeItem("lang"); }
            }
        );
    }

    setLanguage = (language: string | null | undefined) => {
        if (language === undefined)
            this.lang = null;
        else
            this.lang = language;
    };

    setSurveyCode = (code: string | null) => {
        this.currentSurveyCode = code;
    };

    setCurrentSurveyId = (id: number) => {
        this.currentSurveyId = id;
    };

    setCurrentUserSurveyId = (userSurveyId: number) => {
        this.currentUserSurveyId = userSurveyId;
    }

    setSurveyConentTranslations = (translations: SurveyContentTranslations | null) => {
        this.surveyContentTranslations = translations;
    }

    setAppLoaded = () => {
        this.appLoaded = true;
    };

    //gets the translated survey title value
    getSurveyTitle = (survey: SurveyDto): string => {
        if (this.lang == null || survey == null || survey.surveyTranslations == null)
            return survey.title;

        let query = this.lang.toLowerCase();
        let translation = survey.surveyTranslations.find(t => t.language.toLowerCase() === query);
        if (translation == null || !translation)
            return survey.title;

        return translation.title;
    }

    //gets the translated survey title value
    getSurveyResultTitle = (survey: UserSurveyResultDto): string => {
        if (this.lang == null || survey == null || survey.translations == null)
            return survey.surveyTitle;

        let query = this.lang.toLowerCase();
        let translation = survey.translations.find(t => t.language.toLowerCase() === query);
        if (translation == null || !translation)
            return survey.surveyTitle;

        return translation.title;
    }

    //gets the translated survey body value
    getSurveyDescription = (survey: SurveyDto): string => {
        if (this.lang == null || survey == null || survey.surveyTranslations == null)
            return survey.description;

        let query = this.lang.toLowerCase();
        let translation = survey.surveyTranslations.find(t => t.language.toLowerCase() === query);
        if (translation == null || !translation)
            return survey.description;

        return translation.body;
    }

    //gets the translated question body value
    getQuestionBody = (question: QuestionDto): string => {
        if (!question || question == null) return '';
        if (this.lang == null
            || this.surveyContentTranslations == null
            || this.surveyContentTranslations.questions == null)
            return question.body;

        let translation = this.surveyContentTranslations.questions.find(t => t.questionId === question.id);
        if (translation == null || !translation)
            return question.body;

        return translation.body;
    }

    //gets the translated question title value
    getQuestionTitle = (question: QuestionDto): string => {
        if (!question || question == null) return '';
        if (this.lang == null
            || this.surveyContentTranslations == null
            || this.surveyContentTranslations.questions == null)
            return question.title;

        let translation = this.surveyContentTranslations.questions.find(t => t.questionId === question.id);
        if (translation == null || !translation)
            return question.title;

        return translation.title != null ? translation.title : question.title;
    }

    //gets the translated user question body value
    getuserSurveyQuestionBody = (question: UserSurveyQuestionResultDto): string => {
        if (!question || question == null) return '';
        if (this.lang == null
            || this.surveyContentTranslations == null
            || this.surveyContentTranslations.questions == null)
            return question.questionBody;

        let translation = this.surveyContentTranslations.questions.find(t => t.questionId === question.questionId);
        if (translation == null || !translation)
            return question.questionBody;

        return translation.body;
    }

    //gets the translated question choice value
    getQuestionChoiceBody = (choice: QuestionChoiceDto): string => {
        if (!choice || choice == null) return '';
        if (this.lang == null
            || this.surveyContentTranslations == null
            || this.surveyContentTranslations.questionChoices == null)
            return choice.body;

        let translation = this.surveyContentTranslations.questionChoices.find(t => t.questionChoiceId === choice.id);
        if (translation == null || !translation)
            return choice.body;

        return translation.body;
    }

    //gets the translated question choice value
    getUserSurveyQuestionChoiceBody = (choice: UserSurveyQuestionResultDto): string => {
        if (!choice || choice == null) return '';
        if (this.lang == null
            || this.surveyContentTranslations == null
            || this.surveyContentTranslations.questionChoices == null)
            return choice.userAnswer;

        let translation = this.surveyContentTranslations.questionChoices.find(t => t.questionChoiceId === choice.userAnswerChoiceId);
        if (translation == null || !translation)
            return choice.userAnswer;

        return translation.body;
    }

    //gets the translated question choice tip value
    getUserSurveyQuestionChoiceTip = (choice: UserSurveyQuestionResultDto): string => {
        if (!choice || choice == null) return '';
        if (this.lang == null
            || this.surveyContentTranslations == null
            || this.surveyContentTranslations.questionChoices == null)
            return choice.tip;

        let translation = this.surveyContentTranslations.questionChoices.find(t => t.questionChoiceId === choice.userAnswerChoiceId);
        if (translation == null || !translation)
            return choice.tip;

        return translation.tip ? translation.tip : choice.tip;
    }

    //gets the translated question choice level up value
    getUserSurveyQuestionChoiceLevelUp = (choice: UserSurveyQuestionResultDto): string => {
        if (!choice || choice == null) return '';
        if (this.lang == null
            || this.surveyContentTranslations == null
            || this.surveyContentTranslations.questionChoices == null)
            return choice.levelUp;

        //console.log(choice.userAnswerChoiceId);
        let translation = this.surveyContentTranslations.questionChoices.find(t => t.questionChoiceId === choice.userAnswerChoiceId);
        if (translation == null || !translation)
            return choice.levelUp;

        return translation.levelUp ? translation.levelUp : choice.levelUp;
    }

    //gets the translated survey section translations value
    getSurveySectionTitle = (section: SurveySectionDto | null | undefined): string => {
        if (!section || section == null)
            return "";

        if (!this.surveyContentTranslations
            || !this.surveyContentTranslations.sections
            || this.surveyContentTranslations.sections == null
            || this.surveyContentTranslations.sections.length === 0)
            return section.title;

        let translation = this.surveyContentTranslations.sections.find(t => t.surveySectionId === section.id);
        if (translation == null || !translation)
            return section.title;

        return translation.title;
    }

    //gets the translated survey section translations value
    getUserSurveySectionTitle = (section: UserSurveySectionResultDto | null | undefined): string => {
        if (!section || section == null)
            return "";

        if (!this.surveyContentTranslations
            || !this.surveyContentTranslations.sections
            || this.surveyContentTranslations.sections == null
            || this.surveyContentTranslations.sections.length === 0)
            return section.title;

        let translation = this.surveyContentTranslations.sections.find(t => t.surveySectionId === section.sectionId);
        if (translation == null || !translation)
            return section.title;

        return translation.title;
    }

    //gets the translated survey section translations value
    getSurveySectionDescription = (section: SurveySectionDto | undefined | null): string => {
        if (!section || section == null)
            return "";

        if (!this.surveyContentTranslations
            || !this.surveyContentTranslations.sections
            || this.surveyContentTranslations.sections == null
            || this.surveyContentTranslations.sections.length === 0)
            return section.description;

        let translation = this.surveyContentTranslations.sections.find(t => t.surveySectionId === section.id);
        if (translation == null || !translation)
            return section.description;

        return translation.description;
    }

    //gets the translated survey section translations value
    getUserSurveySectionGraphText = (section: UserSurveySectionResultDto | null | undefined): string => {
        if (!section || section == null)
            return "";

        if (!this.surveyContentTranslations
            || !this.surveyContentTranslations.sections
            || this.surveyContentTranslations.sections == null
            || this.surveyContentTranslations.sections.length === 0)
            return section.title;

        let translation = this.surveyContentTranslations.sections.find(t => t.surveySectionId === section.sectionId);

        if (translation == null || !translation)
            return section.title;

        return translation.graphText;
    }
}